import { createFiltersRequestPayload } from './components/Filters/Filters.utils';

const extractListValue = item => {
  if (typeof item === 'string') return item;
  if (typeof item === 'number') return String(item);
  return item?.value ?? item?.title ?? item?.label ?? item?.name ?? null;
};

const normalizeBasics = (resumeBasics = {}, topBasics = {}) => {
  const emails = (() => {
    if (resumeBasics.emails?.length) return resumeBasics.emails;
    if (topBasics.email) return [topBasics.email];
    return [];
  })();

  const phoneNumbers = (() => {
    if (resumeBasics.phone_numbers?.length) return resumeBasics.phone_numbers;
    if (topBasics.phones?.length)
      return topBasics.phones.map(p => p?.fullNumber || p?.phoneNumber).filter(Boolean);
    return [];
  })();

  const addressParts = (() => {
    if (resumeBasics.address) return { computed: resumeBasics.address };
    if (topBasics.location?.zip) return { computed: topBasics.location.zip };
    if (topBasics.location?.city) {
      const suffix = topBasics.location?.state?.slug ? `, ${topBasics.location.state.slug}` : '';
      return { computed: `${topBasics.location.city}${suffix}` };
    }
    return { computed: '' };
  })();

  return {
    ...resumeBasics,
    first_name: resumeBasics.first_name || topBasics.firstName || topBasics.first_name || '',
    last_name: resumeBasics.last_name || topBasics.lastName || topBasics.last_name || '',
    emails,
    phone_numbers: phoneNumbers,
    address: addressParts.computed,
    address_components: resumeBasics.address_components || {
      found_match: Boolean(topBasics.location?.zip || topBasics.location?.city),
      city_title: topBasics.location?.city?.title || topBasics.location?.city,
      state_slug: topBasics.location?.state?.slug,
      zip_title: topBasics.location?.zip,
      country_slug: topBasics.location?.country?.slug
    }
  };
};

export const normalizeResumeSearchItem = item => {
  if (!item) return null;
  const resumeData = Array.isArray(item.resume) && item.resume.length > 0 ? item.resume[0] : {};
  const resumeBasics = resumeData.basics || {};
  const topBasics = item.basics || {};

  const basics = normalizeBasics(resumeBasics, topBasics);

  const profileSkills = resumeBasics.skills || [];
  const topKeywords = item.keywords || [];
  const skills = profileSkills.length
    ? profileSkills.map(extractListValue).filter(Boolean)
    : topKeywords.map(extractListValue).filter(Boolean);

  const experiences = resumeData.professional_experiences?.length
    ? resumeData.professional_experiences
    : item.datasheet?.data?.candidateBackground?.employmentHistoryItems ?? [];

  const educations = resumeData.educations?.length
    ? resumeData.educations
    : item.datasheet?.data?.candidateBackground?.educationItems ?? [];

  const certifications = resumeData.certifications_and_licenses?.length
    ? resumeData.certifications_and_licenses
    : item.datasheet?.data?.candidateBackground?.licensesAndCertificationsItems ?? [];

  const associations = resumeData.professional_associations_and_memberships || [];

  const qualification = item.datasheet?.data?.qualification || {};

  const normalizeCandidateField = obj => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (typeof obj === 'object') return obj;
    return null;
  };

  return {
    candidate: {
      id: item.id,
      type: qualification.candidateType
        ? { id: qualification.candidateType.id, title: qualification.candidateType.title }
        : null,
      status: qualification.candidateStatus
        ? { id: qualification.candidateStatus.id, title: qualification.candidateStatus.title }
        : null,
      functionalTitle: normalizeCandidateField(topBasics.functionalTitle),
      industry: normalizeCandidateField(topBasics.industry),
      specialty: normalizeCandidateField(topBasics.specialty),
      subspecialty: normalizeCandidateField(topBasics.subspecialty),
      last_activity_date: item.lastActivity?.date || null
    },
    profile: {
      basics,
      professional_experiences: experiences,
      educations,
      certifications_and_licenses: certifications,
      professional_associations_and_memberships: associations,
      skills
    },
    meta: {
      file: {
        uploadedAt: item.createdAt || item.updatedAt || null
      }
    }
  };
};

export const normalizeResumeSearchResponse = response => {
  const rawItems = response?.data?.data ?? response?.data ?? [];
  const items = Array.isArray(rawItems) ? rawItems : [];
  const total = response?.data?.total ?? response?.total ?? 0;
  return {
    data: items.map(normalizeResumeSearchItem).filter(Boolean),
    total
  };
};

export const buildSearchProjectQueryParams = ({
  filtersFormValues,
  itemsPerPage,
  totalResults
}) => {
  const totalResultsToSearch = Math.max(totalResults || 0, itemsPerPage || 0);

  return {
    params: {
      search_params: JSON.stringify({
        ...createFiltersRequestPayload(filtersFormValues),
        order: [{ field: 'score', sort: 'DESC' }],
        offset: 0,
        size: totalResultsToSearch
      })
    },
    searchParamsKey: 'search_params'
  };
};
